import {
    List, Datagrid, TextField, DateField, BooleanField,
    Edit, SimpleForm, TextInput, Create, SelectInput,
    BooleanInput, useRecordContext, TopToolbar, ListButton,
} from 'react-admin';
import { Chip, Box } from '@mui/material';
import * as Icons from '@mui/icons-material';
import IconPicker from '../components/IconPicker';
import { useInput } from 'react-admin';

const BadgeTypeField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const typeLabels: Record<string, string> = {
        course_completion: 'Kursabschluss',
        milestone: 'Meilenstein',
        streak: 'Lernserie',
    };

    const colorMap: Record<string, 'primary' | 'secondary' | 'success'> = {
        course_completion: 'primary',
        milestone: 'secondary',
        streak: 'success',
    };

    return (
        <Chip
            label={typeLabels[record.type] || record.type}
            color={colorMap[record.type] || 'default'}
            size="small"
        />
    );
};

const IconField = ({ source }: { source: string }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return <span>-</span>;

    const IconComponent = (Icons as any)[record[source]];
    if (!IconComponent) return <span>{record[source]}</span>;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconComponent sx={{ fontSize: 24, color: '#f5a623' }} />
            <span>{record[source]}</span>
        </Box>
    );
};

const TriggerTypeField = () => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Chip
            label={record.triggerType === 'automatic' ? 'Automatisch' : 'Manuell'}
            variant="outlined"
            size="small"
        />
    );
};

const IconPickerInput = ({ source, label }: { source: string; label?: string }) => {
    const {
        field: { value, onChange },
    } = useInput({ source });

    return <IconPicker value={value} onChange={onChange} label={label} />;
};

export const BadgeList = () => (
    <List>
        <Datagrid rowClick="edit">
            <IconField source="icon" />
            <TextField source="name" label="Name" />
            <BadgeTypeField />
            <TriggerTypeField />
            <TextField source="description" label="Beschreibung" />
            <BooleanField source="isActive" label="Aktiv" />
            <DateField source="createdAt" label="Erstellt" />
        </Datagrid>
    </List>
);

export const BadgeEdit = () => (
    <Edit actions={<TopToolbar><ListButton /></TopToolbar>}>
        <SimpleForm>
            <TextInput source="name" label="Name" fullWidth required />
            <TextInput source="description" label="Beschreibung" multiline rows={2} fullWidth />
            <IconPickerInput source="icon" label="Icon" />

            <SelectInput
                source="type"
                label="Art der Auszeichnung"
                choices={[
                    { id: 'course_completion', name: 'Kursabschluss' },
                    { id: 'milestone', name: 'Meilenstein' },
                    { id: 'streak', name: 'Lernserie' },
                ]}
                fullWidth
            />

            <SelectInput
                source="triggerType"
                label="Auslöser"
                choices={[
                    { id: 'automatic', name: 'Automatisch' },
                    { id: 'manual', name: 'Manuell' },
                ]}
                fullWidth
            />

            <TextInput
                source="triggerConfig"
                label="Trigger-Konfiguration (JSON)"
                multiline
                rows={3}
                fullWidth
                helperText='z.B. {"coursesCompleted": 5} oder {"courseId": 123} oder {"days": 7}'
                parse={(value: string) => {
                    try {
                        return JSON.parse(value);
                    } catch {
                        return value;
                    }
                }}
                format={(value: any) => {
                    if (typeof value === 'object') {
                        return JSON.stringify(value, null, 2);
                    }
                    return value || '';
                }}
            />

            <BooleanInput source="isActive" label="Aktiv" />
        </SimpleForm>
    </Edit>
);

export const BadgeCreate = () => (
    <Create actions={<TopToolbar><ListButton /></TopToolbar>}>
        <SimpleForm>
            <TextInput source="name" label="Name" fullWidth required />
            <TextInput source="description" label="Beschreibung" multiline rows={2} fullWidth />
            <IconPickerInput source="icon" label="Icon" />

            <SelectInput
                source="type"
                label="Art der Auszeichnung"
                choices={[
                    { id: 'course_completion', name: 'Kursabschluss' },
                    { id: 'milestone', name: 'Meilenstein' },
                    { id: 'streak', name: 'Lernserie' },
                ]}
                defaultValue="milestone"
                fullWidth
            />

            <SelectInput
                source="triggerType"
                label="Auslöser"
                choices={[
                    { id: 'automatic', name: 'Automatisch' },
                    { id: 'manual', name: 'Manuell' },
                ]}
                defaultValue="automatic"
                fullWidth
            />

            <TextInput
                source="triggerConfig"
                label="Trigger-Konfiguration (JSON)"
                multiline
                rows={3}
                fullWidth
                helperText='z.B. {"coursesCompleted": 5} oder {"courseId": 123} oder {"days": 7}'
                parse={(value: string) => {
                    try {
                        return JSON.parse(value);
                    } catch {
                        return value;
                    }
                }}
                format={(value: any) => {
                    if (typeof value === 'object') {
                        return JSON.stringify(value, null, 2);
                    }
                    return value || '';
                }}
            />

            <BooleanInput source="isActive" label="Aktiv" defaultValue={true} />
        </SimpleForm>
    </Create>
);
